import {
    centsToFormattedDollars,
    formattedDollarsToCents,
    roundCentsToWholeDollarCents,
} from '@fruitful/fruitils';
import { AccountCategory, type AccountSummary } from '@fruitful/types';

import type { UpdateManualAccountBody } from '../../../entities/membership/hooks/externalAccounts/useUpdateManualAccount';

export const centsToDollarInputDisplay = (cents: number): string => {
    return centsToFormattedDollars(
        roundCentsToWholeDollarCents(cents),
        false,
    ).replace('$', '$ ');
};

export const balanceDollarInputValueFromAccount = (
    account: AccountSummary,
): string => centsToDollarInputDisplay(account.currentBalanceCents ?? 0);

export interface ManualAccountRowFormState {
    name: string;
    userId: string;
    holisticCategory: AccountCategory;
    balanceStr: string;
}

export const formFromManualAccount = (
    account: AccountSummary,
): ManualAccountRowFormState => ({
    name: account.name ?? '',
    userId: account.userId ?? '',
    holisticCategory: account.category ?? AccountCategory.SAVE,
    balanceStr: balanceDollarInputValueFromAccount(account),
});

const normalizedBalanceCents = (balanceStr: string): number | null => {
    const raw = formattedDollarsToCents(balanceStr);
    if (Number.isNaN(raw)) {
        return null;
    }
    return roundCentsToWholeDollarCents(raw);
};

export const isManualAccountRowUnchanged = (
    account: AccountSummary,
    form: ManualAccountRowFormState,
): boolean => {
    const baseline = formFromManualAccount(account);
    if (form.name.trim() !== baseline.name.trim()) {
        return false;
    }
    if (form.userId !== baseline.userId) {
        return false;
    }
    if (form.holisticCategory !== baseline.holisticCategory) {
        return false;
    }
    const formCents = normalizedBalanceCents(form.balanceStr);
    const baseCents = normalizedBalanceCents(baseline.balanceStr);
    if (formCents === null || baseCents === null) {
        return form.balanceStr.trim() === baseline.balanceStr.trim();
    }
    return formCents === baseCents;
};

export const isManualAccountNameInvalid = (name: string): boolean =>
    name.trim().length === 0;

/** True when balance text is empty or does not parse to a whole-dollar cent amount. */
export const isManualAccountBalanceInvalid = (balanceStr: string): boolean =>
    normalizedBalanceCents(balanceStr) === null;

export const validateManualAccountRowForPersist = (
    form: ManualAccountRowFormState,
): { ok: true } | { ok: false } => {
    if (isManualAccountNameInvalid(form.name)) {
        return { ok: false };
    }
    if (!form.userId) {
        return { ok: false };
    }
    if (isManualAccountBalanceInvalid(form.balanceStr)) {
        return { ok: false };
    }
    return { ok: true };
};

export const buildUpdateManualBodyFromRow = (
    account: AccountSummary,
    form: ManualAccountRowFormState,
): UpdateManualAccountBody | null => {
    if (account.userId == null && form.userId === '') {
        return null;
    }
    const cents = normalizedBalanceCents(form.balanceStr);
    if (cents === null) {
        return null;
    }
    const userId = form.userId || account.userId;
    if (userId == null) {
        return null;
    }
    return {
        id: account.id,
        userId,
        name: form.name.trim(),
        amount: cents,
        holisticCategory: form.holisticCategory.toUpperCase(),
    };
};
